import React from 'react';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';

import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

export const REASON_OPTIONS = [
  'REOPEN_DISPUTE_PERIOD',
  'CORRECTION_OF_INSPECTION_ERROR',
  'INSPECTION_EDIT_CLIENT_REQUESTED',
  'CHANGE_ORDER_INFORMATION',
  'PULL_PROPERTY_MODEL',
  'OTHER',
];

const INITIAL_VALUES = { reason: '', description: '' };

const MakeEditableComponent = ({ error, handleSubmit, validationSchema, intl, closeModal }) => {
  const options = React.useMemo(
    () =>
      REASON_OPTIONS.map(option => ({
        key: option,
        label: intl.formatMessage({ id: `RETURN_REASON.${option}` }),
      })),
    [intl]
  );

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-[#153253]">
          {intl.formatMessage({ id: 'RETURN_ORDER' })}
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          {intl.formatMessage({ id: 'RETURN_ORDER_SUBTITLE' })}
        </p>
      </div>

      <Formik
        initialValues={INITIAL_VALUES}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
      {({ errors, touched, submitCount, isSubmitting, handleChange, setFieldValue, setFieldTouched, values }) => {
        const reasonError = errors.reason && (touched.reason || submitCount > 0);
        const descriptionError = errors.description && (touched.description || submitCount > 0);

        return (
          <Form className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="reason" className="text-xs font-semibold text-gray-700">
                {intl.formatMessage({ id: 'REASON' })}*
              </Label>
              <Select
                value={values.reason}
                onValueChange={value => {
                  setFieldValue('reason', value);
                  setFieldTouched('reason', true, false);
                }}
              >
                <SelectTrigger
                  id="reason"
                  className={cn(
                    'w-full h-10 border-gray-300 bg-white focus:border-[#5181C1]',
                    reasonError && 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                  )}
                >
                  <SelectValue placeholder={intl.formatMessage({ id: 'SELECT_PLACEHOLDER' })} />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {options.map(({ key, label }) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {reasonError && (
                <p className="text-xs font-medium text-red-500 mt-1">{errors.reason}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="description" className="text-xs font-semibold text-gray-700">
                {intl.formatMessage({ id: 'DESCRIPTION' })}
                {values.reason === 'OTHER' && '*'}
              </Label>
              <Textarea
                id="description"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={() => setFieldTouched('description', true)}
                placeholder={intl.formatMessage({ id: 'TYPE_PLACEHOLDER' })}
                className={cn(
                  'min-h-[80px] border-gray-300 focus:border-[#5181C1] focus:ring-[#5181C1]/20',
                  descriptionError && 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
                )}
              />
              {descriptionError && (
                <p className="text-xs font-medium text-red-500 mt-1">{errors.description}</p>
              )}
            </div>

            {error && <p className="text-xs font-medium text-red-500">{error}</p>}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={closeModal}
                disabled={isSubmitting}
                className="inline-flex items-center justify-center min-w-[110px] h-[36px] px-4 border-0 rounded-[3px] bg-[#f44336] hover:bg-[#e53935] text-white text-[0.875rem] font-bold transition-opacity disabled:opacity-50"
              >
                {intl.formatMessage({ id: 'CANCEL' })}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="group relative inline-flex items-center justify-center min-w-[110px] h-[36px] px-4 border-0 rounded-[3px] bg-[#97a500] hover:bg-[#889400] text-white text-[0.875rem] font-bold transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isSubmitting ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-[3px]">
                    <div className="w-[18px] h-[18px] border-2 border-black/15 border-r-black/40 rounded-full animate-spin" />
                  </div>
                ) : (
                  intl.formatMessage({ id: 'SEND' })
                )}
              </button>
            </div>
          </Form>
        );
      }}
    </Formik>
  </div>
  );
};

MakeEditableComponent.propTypes = {
  error: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  validationSchema: PropTypes.object.isRequired,
  intl: PropTypes.object.isRequired,
  closeModal: PropTypes.func.isRequired,
};

MakeEditableComponent.defaultProps = {
  error: null,
};

export default MakeEditableComponent;
